import { useEffect, useRef, useState } from "react";
import { admin, errorMessage } from "../lib/api";
import type { ProductImage, Upload } from "../lib/types";
import { Button } from "./ui";

const MAX_BYTES = 10 << 20; // aceeași limită ca în core-api
const ACCEPT = ["image/jpeg", "image/png", "image/webp"];
const PARALLEL = 2;

// Miniatura unei imagini încărcate pe platformă (varianta de 400 px); URL-urile externe rămân ca atare.
const UPLOADED = /^(.*\/[0-9a-f-]{36}\/[0-9a-f]{32}\/)\d+\.webp$/;
export const thumb = (url: string) => url.replace(UPLOADED, "$1400.webp");

type Pending = { key: string; name: string; preview: string; error?: string };

// ImageManager: imaginile produsului (prima e imaginea principală). Fișierele se încarcă imediat (drag & drop
// sau selecție), dar lista se salvează odată cu produsul. URL-urile externe sunt în continuare acceptate.
export function ImageManager({ images, onChange, onBusy }: {
  images: ProductImage[];
  onChange: (images: ProductImage[]) => void;
  onBusy?: (busy: boolean) => void;
}) {
  const [pending, setPending] = useState<Pending[]>([]);
  const [over, setOver] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [extUrl, setExtUrl] = useState("");
  const input = useRef<HTMLInputElement>(null);
  // Upload-urile se termină asincron: adaugă la lista curentă, nu la cea din momentul pornirii.
  const latest = useRef(images);
  latest.current = images;

  const uploading = pending.some((p) => !p.error);
  useEffect(() => onBusy?.(uploading), [uploading, onBusy]);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  useEffect(() => () => pendingRef.current.forEach((p) => URL.revokeObjectURL(p.preview)), []);

  const patchPending = (key: string, patch: Partial<Pending> | null) =>
    setPending((ps) => {
      if (patch) return ps.map((p) => (p.key === key ? { ...p, ...patch } : p));
      ps.filter((p) => p.key === key && p.preview).forEach((p) => URL.revokeObjectURL(p.preview));
      return ps.filter((p) => p.key !== key);
    });

  async function addFiles(files: File[]) {
    // Previzualizare doar pentru imagini (un fișier de alt tip ar apărea ca imagine stricată).
    const items = files.map((f) => ({ file: f, p: { key: crypto.randomUUID(), name: f.name, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : "" } as Pending }));
    for (const it of items) {
      if (!ACCEPT.includes(it.file.type)) it.p.error = "Format neacceptat (doar JPEG, PNG sau WebP)";
      else if (it.file.size > MAX_BYTES) it.p.error = "Fișierul depășește 10 MB";
    }
    setPending((ps) => [...ps, ...items.map((i) => i.p)]);
    const queue = items.filter((i) => !i.p.error);
    const worker = async () => {
      for (let it = queue.shift(); it; it = queue.shift()) {
        const form = new FormData();
        form.append("file", it.file);
        try {
          const up = await admin.upload<Upload>("/media", form);
          if (!latest.current.some((i) => i.url === up.url)) {
            latest.current = [...latest.current, { url: up.url, alt: "" }];
            onChange(latest.current);
          }
          patchPending(it.p.key, null);
        } catch (e) {
          patchPending(it.p.key, { error: errorMessage(e) });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(PARALLEL, queue.length) }, worker));
  }

  const move = (from: number, to: number) => {
    if (to < 0 || to >= images.length || from === to) return;
    const next = [...images];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    onChange(next);
  };

  const addExternal = () => {
    const url = extUrl.trim();
    if (!/^https?:\/\/\S+$/.test(url)) return;
    if (!images.some((i) => i.url === url)) onChange([...images, { url, alt: "" }]);
    setExtUrl("");
  };

  const isFileDrag = (e: React.DragEvent) => e.dataTransfer.types.includes("Files");

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { if (isFileDrag(e)) { e.preventDefault(); setOver(true); } }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          if (!isFileDrag(e)) return;
          e.preventDefault();
          setOver(false);
          addFiles(Array.from(e.dataTransfer.files));
        }}
        className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center text-sm transition ${over ? "border-brand bg-brand/5" : "border-slate-200 bg-slate-50/60"}`}
      >
        <p className="text-slate-600">Trage fotografiile aici sau</p>
        <Button type="button" size="sm" variant="secondary" onClick={() => input.current?.click()}>Alege fișiere</Button>
        <p className="text-xs text-slate-400">JPEG, PNG sau WebP, maximum 10 MB. Se convertesc automat în WebP (400, 800 și 1600 px).</p>
        <input ref={input} type="file" multiple accept={ACCEPT.join(",")} className="hidden" aria-label="Alege imagini"
          onChange={(e) => { addFiles(Array.from(e.target.files ?? [])); e.target.value = ""; }} />
      </div>

      {(images.length > 0 || pending.length > 0) && (
        <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {images.map((im, i) => (
            <li key={im.url}
              draggable
              onDragStart={(e) => { setDragIdx(i); e.dataTransfer.effectAllowed = "move"; }}
              onDragOver={(e) => { if (dragIdx !== null) e.preventDefault(); }}
              onDrop={(e) => { if (dragIdx !== null) { e.preventDefault(); move(dragIdx, i); } setDragIdx(null); }}
              onDragEnd={() => setDragIdx(null)}
              className={`group relative aspect-square cursor-grab overflow-hidden rounded-lg border bg-white ${i === 0 ? "border-brand ring-1 ring-brand" : "border-slate-200"} ${dragIdx === i ? "opacity-40" : ""}`}
            >
              <img src={thumb(im.url)} alt={im.alt} className="h-full w-full object-cover" draggable={false} />
              {i === 0 && <span className="absolute left-1.5 top-1.5 rounded bg-brand px-1.5 py-0.5 text-[10px] font-bold text-white">Principală</span>}
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-gradient-to-t from-black/60 to-transparent p-1.5 opacity-100 transition sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                <span className="flex gap-1">
                  <IconBtn label="Mută la stânga" disabled={i === 0} onClick={() => move(i, i - 1)}>←</IconBtn>
                  <IconBtn label="Mută la dreapta" disabled={i === images.length - 1} onClick={() => move(i, i + 1)}>→</IconBtn>
                </span>
                <IconBtn label="Șterge imaginea" onClick={() => onChange(images.filter((_, j) => j !== i))}>✕</IconBtn>
              </div>
            </li>
          ))}
          {pending.map((p) => (
            <li key={p.key} className="relative aspect-square overflow-hidden rounded-lg border border-slate-200 bg-white" title={p.name}>
              {p.preview && <img src={p.preview} alt="" className={`h-full w-full object-cover ${p.error ? "opacity-30" : "opacity-60"}`} />}
              {p.error ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-2 text-center">
                  <span className="text-[11px] font-semibold leading-tight text-red-700">{p.error}</span>
                  <button type="button" className="text-[11px] text-slate-600 underline" onClick={() => patchPending(p.key, null)}>Închide</button>
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" aria-label="Se încarcă" />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <input className="input font-mono text-xs" value={extUrl} onChange={(e) => setExtUrl(e.target.value)} placeholder="sau adaugă un URL extern: https://…"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExternal(); } }} />
        <Button type="button" size="sm" variant="secondary" onClick={addExternal} disabled={!/^https?:\/\/\S+$/.test(extUrl.trim())}>Adaugă</Button>
      </div>
      <p className="text-xs text-slate-400">Prima imagine este imaginea principală. Ordinea se schimbă prin tragere sau cu săgețile. Modificările se aplică la „Salvează”.</p>
    </div>
  );
}

function IconBtn({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled}
      className="flex h-6 w-6 items-center justify-center rounded bg-white/90 text-xs font-bold text-slate-700 shadow hover:bg-white disabled:opacity-40">
      {children}
    </button>
  );
}
